import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { User, Building, Lock, Users, Sun, Moon, Plus, Trash2, ShieldCheck, Loader2, CreditCard, Bell } from 'lucide-react';
import SubscriptionTab from '../../components/SubscriptionTab';
import PushSettingsCard from '../../components/PushSettingsCard';

const SettingsPage = () => {
  const { user, setUser } = useAuth();
  const [searchParams] = useSearchParams();
  const forceTab = searchParams.get('cancel') === '1' || searchParams.get('billing') === '1' ? 'subscription' : null;
  const [profile, setProfile] = useState({ name: '', phone: '' });
  const [company, setCompany] = useState({ name: '', org_number: '', address: '' });
  const [passwords, setPasswords] = useState({ current_password: '', new_password: '', confirm: '' });
  const [companyUsers, setCompanyUsers] = useState([]);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', name: '', role: 'worker', password: '', phone: '' });
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('operio-theme') === 'dark');
  const [companyVerified, setCompanyVerified] = useState(true);
  const [verification, setVerification] = useState({
    name: '', org_number: '', vat_number: '', address: '', postal_code: '',
    city: '', country: 'Sverige', contact_person: '', contact_phone: '',
    contact_email: '', default_vat_rate: 25.0, iban: '', bic: '', bankgiro: '', plusgiro: '', payment_terms: '30 dagar netto', profession: '',
  });
  const [verifyLoading, setVerifyLoading] = useState(false);

  useEffect(() => {
    if (user) setProfile({ name: user.name || '', phone: user.phone || '' });
    fetchCompany();
    fetchUsers();
  }, [user]);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('operio-theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('operio-theme', 'light');
    }
  }, [darkMode]);

  const fetchCompany = async () => {
    try {
      const res = await api.getCompany();
      const d = res.data;
      setCompany({ name: d.name || '', org_number: d.org_number || '', address: d.address || '' });
      setCompanyVerified(!!d.is_verified);
      // Pre-fill verification form from existing company data
      setVerification(v => ({
        ...v,
        name: d.name || '', org_number: d.org_number || '',
        vat_number: d.vat_number || '', address: d.address || '',
        postal_code: d.postal_code || '', city: d.city || '',
        country: d.country || 'Sverige', contact_person: d.contact_person || user?.name || '',
        contact_phone: d.contact_phone || user?.phone || '',
        contact_email: d.contact_email || user?.email || '',
        default_vat_rate: d.default_vat_rate || 25.0,
        iban: d.iban || '', bic: d.bic || '', bankgiro: d.bankgiro || '', plusgiro: d.plusgiro || '', payment_terms: d.payment_terms || '30 dagar netto', profession: d.profession || '',
      }));
    } catch (err) { console.error(err); }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.getCompanyUsers();
      setCompanyUsers(res.data || []);
    } catch (err) { console.error(err); }
  };

  const handleSaveProfile = async () => {
    try {
      const res = await api.updateProfile(profile);
      toast.success('Profil uppdaterad');
      if (setUser && res.data) setUser(prev => ({ ...prev, name: res.data.name, phone: res.data.phone }));
    } catch (err) { console.error(err); toast.error('Kunde inte uppdatera profil'); }
  };

  const handleSaveCompany = async () => {
    try {
      await api.updateCompany(company);
      toast.success('Företagsinfo uppdaterad');
    } catch (err) { console.error(err); toast.error('Kunde inte uppdatera företagsinfo'); }
  };

  const handleChangePassword = async () => {
    if (passwords.new_password !== passwords.confirm) { toast.error('Lösenorden matchar inte'); return; }
    if (passwords.new_password.length < 6) { toast.error('Lösenordet måste vara minst 6 tecken'); return; }
    try {
      await api.changePassword({ current_password: passwords.current_password, new_password: passwords.new_password });
      toast.success('Lösenord ändrat!');
      setPasswords({ current_password: '', new_password: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte ändra lösenord');
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.name) { toast.error('Ange namn och e-post'); return; }
    try {
      await api.createCompanyUser(newUser);
      toast.success('Användare skapad!');
      setShowUserDialog(false);
      setNewUser({ email: '', name: '', role: 'worker', password: '', phone: '' });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte skapa användare');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Är du säker på att du vill ta bort denna användare?')) return;
    try {
      await api.deleteCompanyUser(userId);
      toast.success('Användare borttagen');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte ta bort användare');
    }
  };

  const handleVerifyCompany = async () => {
    const required = ['name', 'org_number', 'address', 'postal_code', 'city', 'contact_person', 'contact_phone', 'contact_email'];
    const missing = required.filter(k => !verification[k]);
    if (missing.length) {
      toast.error('Fyll i alla obligatoriska fält');
      return;
    }
    setVerifyLoading(true);
    try {
      await api.verifyCompany(verification);
      toast.success('Företaget verifierat! Alla funktioner är nu upplåsta.');
      setCompanyVerified(true);
      fetchCompany();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte verifiera företaget');
    } finally {
      setVerifyLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Inställningar</h2>

      <Tabs defaultValue={forceTab || (companyVerified ? "profile" : "verification")} className="w-full">
        <TabsList className="bg-card border border-border flex-wrap">
          {!companyVerified && (
            <TabsTrigger value="verification" data-testid="settings-tab-verification" className="text-amber-700">
              <ShieldCheck size={14} className="mr-1.5" /> Verifiera
            </TabsTrigger>
          )}
          <TabsTrigger value="profile" data-testid="settings-tab-profile"><User size={14} className="mr-1.5" /> Profil</TabsTrigger>
          <TabsTrigger value="company" data-testid="settings-tab-company"><Building size={14} className="mr-1.5" /> Företag</TabsTrigger>
          <TabsTrigger value="users" data-testid="settings-tab-users"><Users size={14} className="mr-1.5" /> Användare</TabsTrigger>
          <TabsTrigger value="subscription" data-testid="settings-tab-subscription"><CreditCard size={14} className="mr-1.5" /> Prenumeration</TabsTrigger>
          <TabsTrigger value="security" data-testid="settings-tab-security"><Lock size={14} className="mr-1.5" /> Lösenord</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="settings-tab-notifications"><Bell size={14} className="mr-1.5" /> Notiser</TabsTrigger>
          <TabsTrigger value="theme" data-testid="settings-tab-theme">{darkMode ? <Moon size={14} className="mr-1.5" /> : <Sun size={14} className="mr-1.5" />} Utseende</TabsTrigger>
        </TabsList>

        {/* Company Verification */}
        {!companyVerified && (
          <TabsContent value="verification" className="mt-6">
            <div className="bg-card border-2 border-amber-200 rounded-xl p-6 max-w-2xl space-y-5" data-testid="verification-form">
              <div className="flex items-center gap-3 pb-4 border-b border-border">
                <div className="w-10 h-10 bg-amber-50 rounded-full flex items-center justify-center">
                  <ShieldCheck size={20} className="text-amber-600" />
                </div>
                <div>
                  <h3 className="text-base font-bold">Företagsverifiering</h3>
                  <p className="text-xs text-muted-foreground">Fyll i uppgifterna nedan för att låsa upp fakturering, löner och bokföring.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Företagsnamn *</Label>
                  <Input value={verification.name} onChange={e => setVerification(v => ({ ...v, name: e.target.value }))} className="mt-1" data-testid="verify-name" />
                </div>
                <div>
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Org.nummer *</Label>
                  <Input value={verification.org_number} onChange={e => setVerification(v => ({ ...v, org_number: e.target.value }))} placeholder="559123-4567" className="mt-1" data-testid="verify-org-number" />
                </div>
                <div>
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">VAT-nummer</Label>
                  <Input value={verification.vat_number} onChange={e => setVerification(v => ({ ...v, vat_number: e.target.value }))} placeholder="SE559123456701" className="mt-1" data-testid="verify-vat" />
                </div>
                <div>
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Standard moms (%)</Label>
                  <Input type="number" value={verification.default_vat_rate} onChange={e => setVerification(v => ({ ...v, default_vat_rate: parseFloat(e.target.value) || 25 }))} className="mt-1" data-testid="verify-vat-rate" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Adress *</Label>
                  <Input value={verification.address} onChange={e => setVerification(v => ({ ...v, address: e.target.value }))} placeholder="Storgatan 1" className="mt-1" data-testid="verify-address" />
                </div>
                <div>
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Postnummer *</Label>
                  <Input value={verification.postal_code} onChange={e => setVerification(v => ({ ...v, postal_code: e.target.value }))} placeholder="123 45" className="mt-1" data-testid="verify-postal" />
                </div>
                <div>
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Stad *</Label>
                  <Input value={verification.city} onChange={e => setVerification(v => ({ ...v, city: e.target.value }))} placeholder="Stockholm" className="mt-1" data-testid="verify-city" />
                </div>
                <div>
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Land</Label>
                  <Input value={verification.country} onChange={e => setVerification(v => ({ ...v, country: e.target.value }))} className="mt-1" data-testid="verify-country" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Kontaktperson *</Label>
                  <Input value={verification.contact_person} onChange={e => setVerification(v => ({ ...v, contact_person: e.target.value }))} className="mt-1" data-testid="verify-contact-person" />
                </div>
                <div>
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Telefon *</Label>
                  <Input value={verification.contact_phone} onChange={e => setVerification(v => ({ ...v, contact_phone: e.target.value }))} placeholder="070-123 45 67" className="mt-1" data-testid="verify-contact-phone" />
                </div>
                <div>
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">E-post *</Label>
                  <Input type="email" value={verification.contact_email} onChange={e => setVerification(v => ({ ...v, contact_email: e.target.value }))} className="mt-1" data-testid="verify-contact-email" />
                </div>
              </div>

              <div className="border-t border-border pt-4 mt-2">
                <Label className="text-sm font-semibold">Företagets rollprofil</Label>
                <p className="text-[11px] text-muted-foreground mt-0.5 mb-3">
                  Styr vilka moduler du ser och hur material-kalkylen presenteras.
                  En arkitekt behöver inte arbetstimmar — en byggare gör det.
                </p>
                <select
                  value={verification.profession}
                  onChange={e => setVerification(v => ({ ...v, profession: e.target.value }))}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  data-testid="verify-profession"
                >
                  <option value="">— Välj profession —</option>
                  <option value="builder">Byggfirma / Snickeri — material + tid + fakturering</option>
                  <option value="architect">Arkitekt — material-kalkyl, spill, miljöpåverkan</option>
                  <option value="civil">Mark &amp; Anläggning — jord, sten, schakt, maskiner</option>
                  <option value="contractor">Totalentreprenör — allt inkluderat (material + arbete + maskiner)</option>
                  <option value="other">Annat — grundfunktioner</option>
                </select>
              </div>
              <div className="border-t border-border pt-4 mt-2">
                <Label className="text-sm font-semibold">Betalningssätt för löneutbetalning (minst ett krävs)</Label>
                <p className="text-[11px] text-muted-foreground mt-0.5 mb-3">
                  IBAN + BIC krävs för att Operio ska kunna generera automatiska SEPA-filer.
                  Om du bara har Bankgiro eller Plusgiro får du en manuell betalningsguide istället.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">IBAN / Bankkonto</Label>
                  <Input value={verification.iban} onChange={e => setVerification(v => ({ ...v, iban: e.target.value }))} placeholder="SE12 3456 7890 1234 5678 9012" className="mt-1" data-testid="verify-iban" />
                  <p className="text-[10px] text-muted-foreground mt-1">För SEPA-utbetalning (rekommenderas)</p>
                </div>
                <div>
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">BIC / SWIFT</Label>
                  <Input value={verification.bic} onChange={e => setVerification(v => ({ ...v, bic: e.target.value.toUpperCase() }))} placeholder="HANDSESS / ESSESESS / NDEASESS" className="mt-1 uppercase" data-testid="verify-bic" />
                  <p className="text-[10px] text-muted-foreground mt-1">Handelsbanken: HANDSESS · SEB: ESSESESS · Nordea: NDEASESS · Swedbank: SWEDSESS</p>
                </div>
                <div>
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Bankgiro</Label>
                  <Input value={verification.bankgiro} onChange={e => setVerification(v => ({ ...v, bankgiro: e.target.value }))} placeholder="5050-1234" className="mt-1" data-testid="verify-bankgiro" />
                  <p className="text-[10px] text-muted-foreground mt-1">För manuell löneutbetalning</p>
                </div>
                <div>
                  <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Plusgiro</Label>
                  <Input value={verification.plusgiro} onChange={e => setVerification(v => ({ ...v, plusgiro: e.target.value }))} placeholder="12 34 56-7" className="mt-1" data-testid="verify-plusgiro" />
                  <p className="text-[10px] text-muted-foreground mt-1">Alternativ till bankgiro</p>
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Betalningsvillkor</Label>
                <select value={verification.payment_terms} onChange={e => setVerification(v => ({ ...v, payment_terms: e.target.value }))}
                  className="w-full h-10 mt-1 rounded-md border border-input bg-background px-3 text-sm" data-testid="verify-payment-terms">
                  <option value="10 dagar netto">10 dagar netto</option>
                  <option value="15 dagar netto">15 dagar netto</option>
                  <option value="30 dagar netto">30 dagar netto</option>
                  <option value="60 dagar netto">60 dagar netto</option>
                </select>
              </div>

              <Button onClick={handleVerifyCompany} disabled={verifyLoading} data-testid="verify-company-submit"
                className="bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white w-full h-11 font-semibold">
                {verifyLoading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Verifierar...</> : 'Verifiera företag'}
              </Button>
            </div>
          </TabsContent>
        )}

        {/* Profile */}
        <TabsContent value="profile" className="mt-6">
          <div className="bg-card border border-border rounded p-6 max-w-lg space-y-4">
            <h3 className="text-base font-bold">Min profil</h3>
            <div>
              <Label>E-post</Label>
              <Input value={user?.email || ''} disabled className="bg-muted" />
              <p className="text-xs text-muted-foreground mt-1">E-post kan inte ändras</p>
            </div>
            <div>
              <Label>Namn</Label>
              <Input value={profile.name} onChange={(e) => setProfile(p => ({ ...p, name: e.target.value }))} data-testid="settings-name" />
            </div>
            <div>
              <Label>Telefon</Label>
              <Input value={profile.phone} onChange={(e) => setProfile(p => ({ ...p, phone: e.target.value }))} placeholder="070-123 45 67" data-testid="settings-phone" />
            </div>
            <Button onClick={handleSaveProfile} className="bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white" data-testid="save-profile-btn">
              Spara profil
            </Button>
          </div>
        </TabsContent>

        {/* Company */}
        <TabsContent value="company" className="mt-6">
          <div className="bg-card border border-border rounded p-6 max-w-lg space-y-4">
            <h3 className="text-base font-bold">Företagsinformation</h3>
            <div>
              <Label>Företagsnamn</Label>
              <Input value={company.name} onChange={(e) => setCompany(c => ({ ...c, name: e.target.value }))} data-testid="settings-company-name" />
            </div>
            <div>
              <Label>Organisationsnummer</Label>
              <Input value={company.org_number} onChange={(e) => setCompany(c => ({ ...c, org_number: e.target.value }))} placeholder="559123-4567" data-testid="settings-org-number" />
            </div>
            <div>
              <Label>Adress</Label>
              <Input value={company.address} onChange={(e) => setCompany(c => ({ ...c, address: e.target.value }))} placeholder="Gatan 1, 123 45 Stad" data-testid="settings-address" />
            </div>
            <Button onClick={handleSaveCompany} className="bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white" data-testid="save-company-btn">
              Spara företagsinfo
            </Button>
          </div>
        </TabsContent>

        {/* Users */}
        <TabsContent value="users" className="mt-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold">Användarkonton</h3>
              <Button onClick={() => setShowUserDialog(true)} className="bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white" data-testid="add-user-btn">
                <Plus size={14} className="mr-1.5" /> Ny användare
              </Button>
            </div>
            <div className="bg-card border border-border rounded">
              {companyUsers.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">Inga användare</div>
              ) : (
                <table className="w-full">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Namn</th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-muted-foreground uppercase">E-post</th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Roll</th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-muted-foreground uppercase">2FA</th>
                      <th className="px-5 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Åtgärd</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {companyUsers.map((u) => (
                      <tr key={u.id} data-testid={`user-row-${u.id}`}>
                        <td className="px-5 py-3 text-sm font-medium">{u.name || '—'}</td>
                        <td className="px-5 py-3 text-sm text-muted-foreground">{u.email}</td>
                        <td className="px-5 py-3 text-sm">
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                            u.role === 'admin' ? 'bg-blue-50 text-blue-700' : u.role === 'superadmin' ? 'bg-purple-50 text-purple-700' : 'bg-green-50 text-green-700'
                          }`}>{u.role}</span>
                        </td>
                        <td className="px-5 py-3 text-sm">{u.totp_enabled ? 'Aktiv' : 'Av'}</td>
                        <td className="px-5 py-3 text-right">
                          {u.id !== user?.id && (
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(u.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-user-${u.id}`}>
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Subscription */}
        <TabsContent value="subscription" className="mt-6">
          <SubscriptionTab />
        </TabsContent>

        {/* Security / Password */}
        <TabsContent value="security" className="mt-6">
          <div className="bg-card border border-border rounded p-6 max-w-lg space-y-4">
            <h3 className="text-base font-bold">Byt lösenord</h3>
            <div>
              <Label>Nuvarande lösenord</Label>
              <Input type="password" value={passwords.current_password} onChange={(e) => setPasswords(p => ({ ...p, current_password: e.target.value }))} data-testid="current-password" />
            </div>
            <div>
              <Label>Nytt lösenord</Label>
              <Input type="password" value={passwords.new_password} onChange={(e) => setPasswords(p => ({ ...p, new_password: e.target.value }))} data-testid="new-password" />
            </div>
            <div>
              <Label>Bekräfta nytt lösenord</Label>
              <Input type="password" value={passwords.confirm} onChange={(e) => setPasswords(p => ({ ...p, confirm: e.target.value }))} data-testid="confirm-password" />
            </div>
            <Button onClick={handleChangePassword} className="bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white" data-testid="change-password-btn">
              Byt lösenord
            </Button>
          </div>
        </TabsContent>

        {/* Notifications */}
        <TabsContent value="notifications" className="mt-6">
          <PushSettingsCard />
        </TabsContent>

        {/* Theme */}
        <TabsContent value="theme" className="mt-6">
          <div className="bg-card dark:bg-zinc-900 border border-border rounded p-6 max-w-lg space-y-4">
            <h3 className="text-base font-bold">Utseende</h3>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Mörkt läge</p>
                <p className="text-xs text-muted-foreground">Byt mellan ljust och mörkt tema</p>
              </div>
              <button
                onClick={() => setDarkMode(!darkMode)}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${darkMode ? 'bg-[hsl(16,100%,55%)]' : 'bg-muted'}`}
                data-testid="dark-mode-toggle"
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-card transition-transform shadow-sm ${darkMode ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2">
              <button
                onClick={() => setDarkMode(false)}
                className={`border rounded p-4 text-center transition-all ${!darkMode ? 'border-[hsl(16,100%,55%)] bg-[hsl(16,100%,55%)]/5' : 'border-border hover:border-muted-foreground'}`}
                data-testid="theme-light-btn"
              >
                <Sun size={24} className="mx-auto mb-2 text-yellow-500" />
                <p className="text-sm font-medium">Ljust</p>
              </button>
              <button
                onClick={() => setDarkMode(true)}
                className={`border rounded p-4 text-center transition-all ${darkMode ? 'border-[hsl(16,100%,55%)] bg-[hsl(16,100%,55%)]/5' : 'border-border hover:border-muted-foreground'}`}
                data-testid="theme-dark-btn"
              >
                <Moon size={24} className="mx-auto mb-2 text-indigo-500" />
                <p className="text-sm font-medium">Mörkt</p>
              </button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create User Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle>Ny användare</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Namn *</Label>
              <Input value={newUser.name} onChange={(e) => setNewUser(u => ({ ...u, name: e.target.value }))} data-testid="new-user-name" />
            </div>
            <div>
              <Label>E-post *</Label>
              <Input type="email" value={newUser.email} onChange={(e) => setNewUser(u => ({ ...u, email: e.target.value }))} data-testid="new-user-email" />
            </div>
            <div>
              <Label>Roll</Label>
              <select value={newUser.role} onChange={(e) => setNewUser(u => ({ ...u, role: e.target.value }))}
                className="w-full h-10 px-3 border border-input rounded text-sm bg-background" data-testid="new-user-role">
                <option value="worker">Arbetare</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <Label>Lösenord</Label>
              <Input value={newUser.password} onChange={(e) => setNewUser(u => ({ ...u, password: e.target.value }))} data-testid="new-user-password" />
              <p className="text-xs text-muted-foreground mt-1">Ange ett lösenord för den nya användaren</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserDialog(false)}>Avbryt</Button>
            <Button onClick={handleCreateUser} className="bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white" data-testid="create-user-submit">
              Skapa användare
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
